import React, { useState, useEffect } from "react";
import { server } from "../common";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import { AiFillDelete, AiOutlineSearch, AiOutlineDownload, AiOutlineReload } from "react-icons/ai";
import { FaUserPlus, FaEnvelope, FaRegComments, FaCalendarAlt } from "react-icons/fa";

const VolunteerList = () => {
    const [volunteers, setVolunteers] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchVolunteers();
    }, []);

    const fetchVolunteers = () => {
        setLoading(true);
        server
            .get("/volunteer/getallvolunteers")
            .then((res) => {
                // Ensure res.data is an array
                const data = Array.isArray(res.data) ? res.data : [];
                // Sort by ID descending so newest are on top
                data.sort((a, b) => b.id - a.id);
                setVolunteers(data);
            })
            .catch((err) => {
                console.error("Error fetching volunteers:", err);
                toast.error("Failed to fetch volunteer applications.");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleDelete = (id, name) => {
        Swal.fire({
            title: "Are you sure?",
            text: `You are about to delete volunteer application of ${name}.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!"
        }).then((result) => {
            if (result.isConfirmed) {
                server
                    .delete(`/volunteer/deletevolunteer/${id}`)
                    .then((res) => {
                        toast.success("Volunteer record deleted successfully.");
                        fetchVolunteers();
                    })
                    .catch((err) => {
                        console.error("Error deleting volunteer:", err);
                        toast.error("Failed to delete volunteer record.");
                    });
            }
        });
    };

    const exportToExcel = () => {
        if (volunteers.length === 0) {
            toast.warn("No data to export.");
            return;
        }

        const dataToExport = volunteers.map((vol, index) => ({
            "S.No.": index + 1,
            "ID": vol.id,
            "Full Name": vol.fullname,
            "Email": vol.email,
            "Message": vol.message,
            "Submission Date": vol.created_at ? new Date(vol.created_at).toLocaleString() : "-"
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Volunteer Applications");
        
        // Auto-fit columns
        const maxLenProps = Object.keys(dataToExport[0] || {}).map(key => ({
            wch: Math.max(key.length + 3, ...dataToExport.map(row => String(row[key] || "").length + 3))
        }));
        worksheet["!cols"] = maxLenProps;

        XLSX.writeFile(workbook, "Volunteer_Applications_Report.xlsx");
        toast.success(`Exported ${volunteers.length} records successfully!`);
    };

    // Filter logic
    const filteredVolunteers = volunteers.filter(vol => {
        const query = searchQuery.toLowerCase();
        return (
            (vol.fullname || "").toLowerCase().includes(query) ||
            (vol.email || "").toLowerCase().includes(query) ||
            (vol.message || "").toLowerCase().includes(query)
        );
    });

    return (
        <div className="container-fluid py-4 px-md-5" style={{ background: "#f8fafc", minHeight: "100vh" }}>
            {/* Header section with modern design */}
            <div 
                className="card border-0 shadow-sm mb-4 p-4 text-white" 
                style={{ 
                    background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                    borderRadius: "16px"
                }}
            >
                <div className="row align-items-center">
                    <div className="col-md-8">
                        <h3 className="fw-bold mb-1">🤝 Volunteer Applications</h3>
                        <p className="mb-0 opacity-90">Manage, review, and export all volunteer signups and user messages.</p>
                    </div>
                    <div className="col-md-4 text-md-end mt-3 mt-md-0">
                        <div className="d-inline-flex gap-3 text-black bg-white bg-opacity-20 px-3 py-2 rounded-3 border border-white border-opacity-10">
                            <div className="text-center">
                                <span className="d-block fw-bold fs-4 line-height-1">{volunteers.length}</span>
                                <small className="opacity-90" style={{ fontSize: "11px" }}>Total Applicants</small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filter and controls bar */}
            <div className="card border-0 shadow-sm p-4 mb-4" style={{ borderRadius: "12px" }}>
                <div className="row g-3 align-items-center">
                    <div className="col-md-6 col-lg-8">
                        <div className="input-group">
                            <span className="input-group-text bg-light border-end-0 text-muted">
                                <AiOutlineSearch size={18} />
                            </span>
                            <input
                                type="text"
                                className="form-control bg-light border-start-0 ps-0"
                                placeholder="Search by name, email, or message content..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="col-md-6 col-lg-4 d-flex gap-2 justify-content-md-end">
                        <button 
                            className="btn btn-outline-secondary d-flex align-items-center gap-2 fw-semibold px-3 py-2 rounded-3 transition-all"
                            onClick={fetchVolunteers}
                            disabled={loading}
                        >
                            <AiOutlineReload className={loading ? "animate-spin" : ""} size={16} /> Refresh
                        </button>
                        <button 
                            className="btn text-white d-flex align-items-center gap-2 fw-semibold px-3 py-2 rounded-3 transition-all"
                            style={{ backgroundColor: "#11998e", border: "none" }}
                            onClick={exportToExcel}
                            disabled={loading || volunteers.length === 0}
                        >
                            <AiOutlineDownload size={18} /> Export Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* Main database table */}
            <div className="card border-0 shadow-sm p-4" style={{ borderRadius: "12px" }}>
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-success" role="status" style={{ width: "3rem", height: "3rem" }}>
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted fw-semibold">Fetching volunteer submissions...</p>
                    </div>
                ) : (
                    <>
                        <div className="table-responsive">
                            <table className="table table-hover border align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th style={{ width: "60px" }} className="text-center">S.No.</th>
                                        <th>Applicant Info</th>
                                        <th>Email Address</th>
                                        <th>Message / Query</th>
                                        <th style={{ width: "160px" }}>Date & Time</th>
                                        <th style={{ width: "80px" }} className="text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredVolunteers.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="text-center text-muted py-5">
                                                <div className="mb-3">🤝</div>
                                                <h5 className="fw-semibold">No volunteers found</h5>
                                                <p className="mb-0 small text-muted">Try adjusting your search query or refresh the page.</p>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredVolunteers.map((vol, index) => (
                                            <tr key={vol.id}>
                                                <td className="text-center fw-bold text-muted">{index + 1}</td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div className="bg-success-subtle text-success rounded-circle d-flex align-items-center justify-content-center fw-bold" style={{ width: "35px", height: "35px", backgroundColor: "rgba(56, 239, 125, 0.15)", color: "#11998e" }}>
                                                            {vol.fullname ? vol.fullname.charAt(0).toUpperCase() : "V"}
                                                        </div>
                                                        <div className="fw-bold text-dark">{vol.fullname}</div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2 text-secondary">
                                                        <FaEnvelope className="text-muted" size={14} />
                                                        <a href={`mailto:${vol.email}`} className="text-decoration-none text-dark fw-semibold hover-underline" style={{ fontSize: "13px" }}>
                                                            {vol.email}
                                                        </a>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-start gap-2" style={{ maxWidth: "450px" }}>
                                                        <FaRegComments className="text-muted mt-1 flex-shrink-0" size={14} />
                                                        <span className="text-dark bg-light p-2 rounded-3 d-inline-block text-wrap" style={{ fontSize: "13px", wordBreak: "break-word" }}>
                                                            {vol.message}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: "12px" }}>
                                                        <FaCalendarAlt size={12} />
                                                        <span>{vol.created_at ? new Date(vol.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "-"}</span>
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <button 
                                                        className="btn btn-outline-danger btn-sm rounded-3 p-2 border-0 hover-bg-danger-subtle"
                                                        onClick={() => handleDelete(vol.id, vol.fullname)}
                                                        title="Delete Application"
                                                    >
                                                        <AiFillDelete size={18} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {filteredVolunteers.length > 0 && (
                            <div className="text-muted text-end mt-3 small fw-semibold">
                                Showing {filteredVolunteers.length} of {volunteers.length} volunteer submissions
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default VolunteerList;
